import React, { useState, useEffect } from "react";
import { announcementService } from "../services/announcementService";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://newgardenbackend.amcfest.in/api";

const AnnouncementManagement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterByDate, setFilterByDate] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [viewImageModal, setViewImageModal] = useState({
    show: false,
    src: "",
    title: "",
  });

  // Local placeholder image (SVG data URI)
  const placeholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect width='80' height='80' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='10' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
  const placeholderImageLarge = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%236b7280'%3EImage Not Found%3C/text%3E%3C/svg%3E";

  // Helper function to get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // Replace localhost URLs with production URL
    if (imagePath.includes("localhost:4001")) {
      imagePath = imagePath.replace(/http:\/\/localhost:4001/g, "https://newgardenbackend.amcfest.in");
    }
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    
    // Get base URL without /api
    const baseUrl = API_BASE_URL.replace("/api", "");
    // If it starts with /, it's a relative path from the base
    if (imagePath.startsWith("/")) {
      return `${baseUrl}${imagePath}`;
    }
    // Otherwise, assume it's relative to the base
    return `${baseUrl}/${imagePath}`;
  };

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const data = await announcementService.getAllAnnouncements();
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading announcements:", error);
      alert("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadAnnouncements();
      return;
    }
    try {
      setLoading(true);
      const response = await announcementService.searchAnnouncement(
        searchQuery
      );
      setAnnouncements(response.data || response || []);
    } catch (error) {
      console.error("Error searching announcements:", error);
      alert("Failed to search announcements");
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async () => {
    if (!filterByDate) {
      loadAnnouncements();
      return;
    }
    try {
      setLoading(true);
      const response = await announcementService.filterAnnouncement(
        filterByDate
      );
      setAnnouncements(response.data || response || []);
    } catch (error) {
      console.error("Error filtering announcements:", error);
      alert("Failed to filter announcements");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description || "");
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      if (editingAnnouncement) {
        await announcementService.updateAnnouncement(
          editingAnnouncement.id,
          formDataToSend
        );
        alert("Announcement updated successfully");
      } else {
        await announcementService.addAnnouncement(formDataToSend);
        alert("Announcement added successfully");
      }

      resetForm();
      loadAnnouncements();
    } catch (error) {
      console.error("Error saving announcement:", error);
      alert(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to save announcement"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title || "",
      description: announcement.description || "",
      image: null,
    });
    setImagePreview(announcement.image ? getImageUrl(announcement.image) : null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this announcement?")) {
      return;
    }
    try {
      setLoading(true);
      await announcementService.deleteAnnouncement(id);
      alert("Announcement deleted successfully");
      loadAnnouncements();
    } catch (error) {
      console.error("Error deleting announcement:", error);
      alert("Failed to delete announcement");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: "", description: "", image: null });
    setImagePreview(null);
    setEditingAnnouncement(null);
    setShowForm(false);
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-800">
            Announcement Management
          </h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            {showForm ? "Cancel" : "Add New Announcement"}
          </button>
        </div>

        {/* Search and Filter */}
        <div className="mb-6 flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by title or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={handleSearch}
            className="bg-orange-400 text-white px-6 py-2 rounded-lg hover:bg-orange-700"
          >
            Search
          </button>
          <select
            value={filterByDate}
            onChange={(e) => {
              setFilterByDate(e.target.value);
              if (e.target.value) handleFilter();
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Filter by Date</option>
            <option value="2days">Last 2 Days</option>
            <option value="month">Last Month</option>
            <option value="3months">Last 3 Months</option>
            <option value="6months">Last 6 Months</option>
          </select>
          {(searchQuery || filterByDate) && (
            <button
              onClick={() => {
                setSearchQuery("");
                setFilterByDate("");
                loadAnnouncements();
              }}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
            >
              Clear
            </button>
          )}
        </div>

        {/* Form */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 rounded-lg shadow-lg">
              <h2 className="text-xl font-semibold mb-4">
                {editingAnnouncement
                  ? "Edit Announcement"
                  : "Add New Announcement"}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter announcement title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter announcement description (optional)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image{" "}
                    {!editingAnnouncement && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    required={!editingAnnouncement}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  {(imagePreview ||
                    (editingAnnouncement && editingAnnouncement.image)) && (
                    <div className="mt-4">
                      <img
                        src={
                          imagePreview ||
                          getImageUrl(editingAnnouncement.image)
                        }
                        alt="Preview"
                        className="max-w-xs h-48 object-cover rounded-lg border border-gray-300 shadow-md"
                        onError={(e) => {
                          // Silently handle error - use local placeholder
                          e.target.src = placeholderImageLarge;
                          e.target.onerror = null; // Prevent infinite loop
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading
                      ? "Saving..."
                      : editingAnnouncement
                      ? "Update"
                      : "Add"}{" "}
                    Announcement
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Announcements List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading && !showForm ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading announcements...</p>
            </div>
          ) : announcements.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No announcements found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-teal-600 to-emerald-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {announcements.map((announcement) => (
                    <tr key={announcement.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {announcement.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {announcement.image ? (
                          <img
                            src={getImageUrl(announcement.image)}
                            alt={announcement.title || "Announcement"}
                            className="h-20 w-20 object-cover rounded-lg border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity shadow-md"
                            onClick={() => {
                              const imgSrc = getImageUrl(announcement.image);
                              setViewImageModal({
                                show: true,
                                src: imgSrc,
                                title:
                                  announcement.title || "Announcement Image",
                              });
                            }}
                            onError={(e) => {
                              // Silently handle error - use local placeholder
                              e.target.src = placeholderImage;
                              e.target.onclick = null; // Disable click on placeholder
                              e.target.onerror = null; // Prevent infinite loop
                            }}
                          />
                        ) : (
                          <div className="h-20 w-20 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500 border border-gray-300">
                            No Image
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {announcement.title || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                        <div
                          className="truncate"
                          title={announcement.description || "N/A"}
                        >
                          {announcement.description || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {announcement.Date
                          ? new Date(announcement.Date).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {announcement.created_at
                          ? new Date(announcement.created_at).toLocaleString()
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(announcement)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(announcement.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Image View Modal */}
        {viewImageModal.show && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() =>
              setViewImageModal({ show: false, src: "", title: "" })
            }
          >
            <div
              className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">
                  {viewImageModal.title}
                </h3>
                <button
                  onClick={() =>
                    setViewImageModal({ show: false, src: "", title: "" })
                  }
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="p-6">
                <img
                  src={viewImageModal.src}
                  alt={viewImageModal.title}
                  className="max-w-full h-auto rounded-lg shadow-lg"
                  onError={(e) => {
                    // Silently handle error - use local placeholder
                    e.target.src = placeholderImageLarge;
                    e.target.onerror = null; // Prevent infinite loop
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementManagement;
